import 'moment-duration-format';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { IMAGE_CONFIG } from '@angular/common';
import * as Sentry from '@sentry/angular';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StoreModule } from '@ngrx/store';
import * as fromApp from './store/app.reducer';
import { EffectsModule } from '@ngrx/effects';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ComponentsComponent } from './components/components.component';
import { LandingComponent } from './landing/landing.component';
import { LoginComponent } from './auth/login/login.component';
import { PasswordResetSuccessComponent } from './auth/password-reset-success/password-reset-success.component';
import { RegisterComponent } from './auth/register/register.component';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ResetPasswordLinkComponent } from './auth/reset-password-link/reset-password-link.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { CompanyComponent } from './components/company/company.component';
import { UsersComponent } from './components/company/users/users.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { ReportsComponent } from './components/reports/reports.component';
import { ReportsDetailsComponent } from './components/reports/reports-details/reports-details.component';
import { ReportComponent } from './components/reports/report/report.component';
import { ReportTypesComponent } from './components/reports/report-types/report-types.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgApexchartsModule } from 'ng-apexcharts';
import { AssignmentsComponent } from './components/assignments/assignments.component';
import { SentryExample } from './sentry-example.component';
import { CreateAssignmentComponent } from './components/assignments/create-assignment/create-assignment.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Step1Component } from './components/assignments/create-assignment/step1/step1.component';
import { Step2Component } from './components/assignments/create-assignment/step2/step2.component';
import { Step3Component } from './components/assignments/create-assignment/step3/step3.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthEffects } from "./auth/store/auth.effects";
import { CompanyEffects } from "./components/company/store/company.effects";
import { SharedEffects } from "./shared-store/shared.effects";
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { AngularFireDatabaseModule } from "@angular/fire/compat/database";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import { NgxSpinnerModule } from "ngx-spinner";
import { ToastrModule } from 'ngx-toastr';
import { GoogleMapsModule } from '@angular/google-maps';
import { Interceptor } from "./interception";
import { AssignmentsEffects } from "./components/assignments/store/assignments.effects";
import { environment } from "../environments/environment";
import { AcceptInviteComponent } from './components/company/accept-invite/accept-invite.component';
import { GroupByPipe } from './pipes/group-by.pipe';
import { ModalComponent } from './shared/modal/modal.component';
import { ProgressBarRequestComponent } from './shared/progress-bar-request/progress-bar-request.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { GroupByDatePipe } from './pipes/group-by-date.pipe';
import { CalendarModule } from '@syncfusion/ej2-angular-calendars';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import localeSv from '@angular/common/locales/sv';
import { registerLocaleData } from '@angular/common';
registerLocaleData(localeSv);
import { AngularEditorModule } from '@kolkov/angular-editor';
import { CreateCompanyComponent } from './components/company/create-company/create-company.component';
import { FinishedComponent } from './components/assignments/assignment-shift-modal/finished/finished.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { FilterPipe } from './pipes/filter.pipe';
import { MarkdownPipe } from './pipes/markdown.pipe';
import { MarkdownModule, MarkedOptions, MARKED_OPTIONS } from 'ngx-markdown';
import { Renderer, type Tokens } from 'marked';
import { MsalModule, MsalService, MSAL_INSTANCE, MsalBroadcastService } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { ProgressSpinnerComponent } from './progress-spinner/progress-spinner.component';
import { SkillDisplayNamePipe } from './shared/pipes/skill-display-name.pipe';
import { MatPaginatorIntl } from '@angular/material/paginator';

/** Web Crypto API is required by MSAL and is only available in secure contexts (HTTPS or localhost). */
function isCryptoAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.crypto?.subtle;
}

/**
 * Minimal stub for when Web Crypto is unavailable (e.g. HTTP on non-localhost).
 * Allows the app to load; Microsoft login will show a friendly error.
 */
function createMsalStub(): PublicClientApplication {
    const unavailable =
        'Microsoft sign-in requires a secure context (HTTPS or http://localhost). Please open this app via HTTPS or localhost.';
    return {
        initialize: () => Promise.resolve(),
        getActiveAccount: () => null,
        setActiveAccount: (_account: AccountInfo | null) => { },
        loginPopup: () => Promise.reject(new Error(unavailable)),
        loginRedirect: () => Promise.resolve(),
        acquireTokenSilent: () => Promise.reject(new Error(unavailable)),
        acquireTokenPopup: () => Promise.reject(new Error(unavailable)),
        acquireTokenRedirect: () => Promise.resolve(),
        logout: () => Promise.resolve(),
        logoutRedirect: () => Promise.resolve(),
        logoutPopup: () => Promise.resolve(),
        getAllAccounts: () => [],
        getAccountByHomeId: () => null,
        getAccountByLocalId: () => null,
        getAccountByUsername: () => null,
        setNavigationClient: () => { },
        addEventCallback: () => null,
        removeEventCallback: () => { },
        addPerformanceCallback: () => null,
        removePerformanceCallback: () => false,
        enableAccountStorageEvents: () => { },
        initializeWrapperLibrary: () => { },
        getConfiguration: () => ({} as any),
    } as unknown as PublicClientApplication;
}
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ConfettiService } from './service/confetti.service';
import { CustomPaginatorIntl } from './shared/services/paginator-intl.service';
export function MSALInstanceFactory(): PublicClientApplication {
    if (!isCryptoAvailable()) {
        console.warn(
            'MSAL: Web Crypto API is not available (e.g. running over HTTP on non-localhost). ' +
            'Microsoft sign-in will be disabled. Use HTTPS or http://localhost to enable it.'
        );
        return createMsalStub();
    }
    return new PublicClientApplication({
        auth: {
            clientId: environment.msalConfig.auth.clientId,
            authority: environment.msalConfig.auth.authority,
            redirectUri: environment.msalConfig.auth.redirectUri
        },
        cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false
        }
    });
}

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
    const loader = new TranslateHttpLoader(http);
    return {
        getTranslation: (lang: string) =>
            loader.getTranslation(lang).pipe(
                catchError(() => of({}))
            )
    };
}

/** Marked (markdown) options used by ngx-markdown for AI responses. H1–H3 render as bold paragraph; only H4–H6 as headings. */
export function markedOptionsFactory(): MarkedOptions {
    const renderer = new Renderer();
    renderer.heading = function (this: Renderer, { tokens, depth }: Tokens.Heading): string {
        const content = this.parser.parseInline(tokens);
        // Downgrade H1-H3 to H4-H6 so chat bubbles keep consistent visual hierarchy.
        const normalizedDepth = depth <= 3 ? depth + 3 : depth;
        const safeDepth = Math.min(Math.max(normalizedDepth, 4), 6);
        return `<h${safeDepth}>${content}</h${safeDepth}>\n`;
    };
    return {
        renderer,
        gfm: true,
        breaks: true,
    };
}

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        RegisterComponent,
        ForgotPasswordComponent,
        ResetPasswordLinkComponent,
        ResetPasswordComponent,
        PasswordResetSuccessComponent,
        ComponentsComponent,
        CompanyComponent,
        UsersComponent,
        ReportsComponent,
        ReportsDetailsComponent,
        ReportComponent,
        ReportTypesComponent,
        AssignmentsComponent,
        CreateAssignmentComponent,
        Step1Component,
        Step2Component,
        Step3Component,
        AcceptInviteComponent,
        GroupByPipe,
        MarkdownPipe,
        ModalComponent,
        ProgressBarRequestComponent,
        GroupByDatePipe,
        CreateCompanyComponent,
        FinishedComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
    imports: [
        SentryExample,
        SkillDisplayNamePipe,
        MatIconModule,
        MatTooltipModule,
        AngularFireModule.initializeApp(environment.firebase),
        AngularFireMessagingModule,
        AngularFireDatabaseModule,
        AngularFireAuthModule,
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        NgxDaterangepickerMd.forRoot(),
        ReactiveFormsModule,
        LandingComponent,
        StoreModule.forRoot(fromApp.appReducer),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: environment.production,
            autoPause: true,
        }),
        EffectsModule.forRoot([
            AuthEffects,
            CompanyEffects,
            AssignmentsEffects,
            SharedEffects,
        ]),
        MatSidenavModule,
        BrowserAnimationsModule,
        MatListModule,
        MatButtonModule,
        NgApexchartsModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatSortModule,
        MatTableModule,
        MatTabsModule,
        MatCardModule,
        MatSelectModule,
        MatBottomSheetModule,
        MatMenuModule,
        NgbModule,
        MatButtonToggleModule,
        GoogleMapsModule,
        NgxSpinnerModule.forRoot({ type: 'ball-scale-pulse' }),
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true,
            closeButton: true,
            progressBar: true
        }),
        CalendarModule,
        TranslateModule.forRoot({
            defaultLanguage: 'sv',
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        // Kolkov Text Editor Module
        AngularEditorModule,
        FullCalendarModule,
        MatButtonModule,
        MatSelectModule, MatCardModule,
        MatListModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatNativeDateModule,
        FilterPipe,
        MsalModule.forRoot(
            MSALInstanceFactory(),
            {
                interactionType: InteractionType.Popup,
                authRequest: {
                    scopes: ['user.read']
                }
            },
            null
        ),
        ProgressSpinnerComponent,
        MatDialogModule,
        MatChipsModule,
        NgbTooltipModule,
        MatMenuModule,
        MarkdownModule.forRoot({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: markedOptionsFactory,
            },
        }),
    ], providers: [
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
        provideNativeDateAdapter(),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: Interceptor,
            multi: true,
        },
        {
            provide: DEFAULT_CURRENCY_CODE, useValue: 'SEK'
        },
        {
            provide: LOCALE_ID,
            useValue: 'sv-SE'
        },
        {
            provide: IMAGE_CONFIG,
            useValue: {
                disableImageSizeWarning: true
            }
        },
        {
            provide: 'GOOGLE_MAPS_API_KEY',
            useValue: 'AIzaSyCKm_wNklUrHWRv9nINRo0uzU8V2nF4t-Y'
        },
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntl
        },
        MsalService,
        MsalBroadcastService,
        ConfettiService
    ]
})
export class AppModule {
}